import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'localization';
import SearchBarAtom from '../../atoms/SearchBar/SearchBarAtom';
import { Toast } from 'primereact/toast';
import { Sphere } from 'types/global';
import { materializePeriod } from '@center-platform/spheres/identity';
import { createRelationship, deleteRelationship } from '@center-platform/spheres/sphere';

import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import TimeframeSidebarMolecule, {
  TimeframeItem,
} from '../../molecules/TimeframeSidebar/TimeframeSidebarMolecule';
import PeriodGridMolecule from '../../molecules/PeriodGrid/PeriodGridMolecule';
import SelectionBarAtom, {
  SelectionBarSelection,
} from '../../atoms/SelectionBar/SelectionBarAtom';
import { PeriodCardData } from '../../atoms/PeriodCard/PeriodCardAtom';

import './PeriodSelectorOrganism.scss';

/** Tracks a selected period across timeframes */
interface SelectionEntry {
  key: string;
  name: string;
  timeframeId: string;
  timeframeName: string;
  period: PeriodCardData | Sphere;
}

/** Maps an existing period sphere to its relationship ID for deselection handling */
export interface ExistingRelationship {
  periodSphereId: string;
  relationshipId: string;
}

export interface PeriodSelectorOrganismProps {
  sphereId: string;
  organizationId: string;
  value?: Sphere[];
  /** Relationship IDs for currently assigned periods — needed to delete deselected ones */
  existingRelationships?: ExistingRelationship[];
  onApply: () => void;
  onCancel: () => void;
}

function periodKey(p: PeriodCardData | Sphere): string {
  return 'key' in p && 'materialized' in p
    ? p.key
    : p.id ?? '';
}

function periodName(p: PeriodCardData | Sphere): string {
  if ('key' in p && 'materialized' in p) return p.name;
  const props = p.properties as Record<string, string> | undefined;
  return props?.name ?? '';
}

const PeriodSelectorOrganism: React.FC<PeriodSelectorOrganismProps> = ({
  sphereId,
  organizationId,
  value = [],
  existingRelationships = [],
  onApply,
  onCancel,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const toastRef = useRef<Toast>(null);

  /* ----- Timeframe state ----- */
  const [activeTimeframe, setActiveTimeframe] = useState<TimeframeItem | null>(
    null,
  );

  /* ----- Selection state (persists across timeframe switches) ----- */
  const [selections, setSelections] = useState<Map<string, SelectionEntry>>(
    () => {
      const map = new Map<string, SelectionEntry>();
      value.forEach((s) => {
        const k = s.id ?? '';
        const props = s.properties as Record<string, string> | undefined;
        map.set(k, {
          key: k,
          name: props?.name ?? '',
          timeframeId: '',
          timeframeName: '',
          period: s,
        });
      });
      return map;
    },
  );

  /* ----- Search state ----- */
  const [searchTerm, setSearchTerm] = useState('');

  /* ----- Apply state ----- */
  const [applying, setApplying] = useState(false);

  /* ----- Derived values ----- */
  const selectedKeys = useMemo(
    () => new Set(selections.keys()),
    [selections],
  );

  const selectionBarItems: SelectionBarSelection[] = useMemo(
    () =>
      Array.from(selections.values()).map((e) => ({
        key: e.key,
        name: e.name,
        timeframeName: e.timeframeName,
      })),
    [selections],
  );

  /* ----- Handlers ----- */
  const handleTimeframeSelect = useCallback((item: TimeframeItem) => {
    setActiveTimeframe(item);
  }, []);

  const handleToggle = useCallback(
    (period: PeriodCardData | Sphere) => {
      const key = periodKey(period);
      setSelections((prev) => {
        const next = new Map(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.set(key, {
            key,
            name: periodName(period),
            timeframeId: activeTimeframe?.id ?? '',
            timeframeName: activeTimeframe?.name ?? '',
            period,
          });
        }
        return next;
      });
    },
    [activeTimeframe],
  );

  const handleRemove = useCallback((key: string) => {
    setSelections((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const handleApply = useCallback(async () => {
    if (!apiClients?.graphClient) return;
    setApplying(true);

    const graphClient = apiClients.graphClient;
    const entries = Array.from(selections.values());
    const existingIds = new Set(value.map((s) => s.id ?? ''));
    let hasError = false;

    /* --- 1. Materialize virtual periods --- */
    const materializeResults = await Promise.allSettled(
      entries.map(async (entry) => {
        const p = entry.period;
        if (!('materialized' in p)) return { entry, sphereId: (p as Sphere).id ?? '' };
        const card = p as PeriodCardData;
        if (card.materialized && card.materializedId) {
          return { entry, sphereId: card.materializedId };
        }
        const result = await materializePeriod(graphClient, {
          rootTimeframeId: entry.timeframeId,
          targetDate: card.startDate,
          targetLevel: card.level as 'year' | 'quarter' | 'month' | 'week',
        });
        return { entry, sphereId: result.periodSphereId };
      }),
    );

    /* --- 2. Create relationships for new periods --- */
    const createResults = await Promise.allSettled(
      materializeResults
        .filter(
          (r): r is PromiseFulfilledResult<{ entry: SelectionEntry; sphereId: string }> =>
            r.status === 'fulfilled',
        )
        .filter((r) => !existingIds.has(r.value.sphereId))
        .map(async (r) => {
          const result = await createRelationship(graphClient, {
            fromSphereId: sphereId,
            toSphereId: r.value.sphereId,
            relationshipType: 'applies_to_period',
          });
          if (!result.success) throw new Error(result.error);
          return { name: r.value.entry.name, sphereId: r.value.sphereId };
        }),
    );

    /* --- 3. Delete relationships for deselected periods --- */
    const selectedSphereIds = new Set(
      materializeResults
        .filter((r): r is PromiseFulfilledResult<{ entry: SelectionEntry; sphereId: string }> => r.status === 'fulfilled')
        .map((r) => r.value.sphereId),
    );
    const toRemove = existingRelationships.filter(
      (r) => !selectedSphereIds.has(r.periodSphereId),
    );
    const deleteResults = await Promise.allSettled(
      toRemove.map(async (r) => {
        const result = await deleteRelationship(graphClient, {
          relationshipId: r.relationshipId,
        });
        if (!result.success) throw new Error(result.error);
      }),
    );

    /* --- 4. Report per-period errors via toast --- */
    const failures: string[] = [];
    materializeResults.forEach((r, i) => {
      if (r.status === 'rejected') {
        hasError = true;
        failures.push(entries[i].name);
      }
    });
    createResults.forEach((r) => {
      if (r.status === 'rejected') {
        hasError = true;
        failures.push(r.reason instanceof Error ? r.reason.message : String(r.reason));
      }
    });
    deleteResults.forEach((r) => {
      if (r.status === 'rejected') {
        hasError = true;
        failures.push(r.reason instanceof Error ? r.reason.message : String(r.reason));
      }
    });

    if (failures.length > 0) {
      toastRef.current?.show({
        severity: 'error',
        summary: t('periodSelector.applyErrors'),
        detail: failures.join(', '),
        life: 5000,
      });
    }

    setApplying(false);
    if (!hasError) {
      onApply();
    }
  }, [
    apiClients,
    selections,
    value,
    existingRelationships,
    sphereId,
    organizationId,
    onApply,
    t,
  ]);

  const handleSidebarError = useCallback(
    (_messageKey: string) => {
      toastRef.current?.show({
        severity: 'error',
        summary: t('periodSelector.fetchTimeframesError'),
        life: 4000,
      });
    },
    [t],
  );

  const handleGridError = useCallback(
    (_messageKey: string) => {
      toastRef.current?.show({
        severity: 'error',
        summary: t('periodSelector.fetchPeriodsError'),
        life: 4000,
      });
    },
    [t],
  );

  return (
    <div className="period-selector-organism">
      <Toast ref={toastRef} />
      {/* Top: unified search */}
      <div className="period-selector-organism__search">
        <SearchBarAtom
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('periodSelector.searchplaceholder')}
        />
      </div>

      {/* Two-panel layout */}
      <div className="period-selector-organism__panels">
        <TimeframeSidebarMolecule
          sphereId={sphereId}
          organizationId={organizationId}
          activeTimeframeId={activeTimeframe?.id}
          onSelect={handleTimeframeSelect}
          searchTerm={searchTerm}
          onError={handleSidebarError}
          className="period-selector-organism__sidebar"
        />
        <PeriodGridMolecule
          timeframeId={activeTimeframe?.id}
          timeframeName={activeTimeframe?.name}
          nestedTimeframes={activeTimeframe?.nestedTimeframes}
          scheduleDefinition={activeTimeframe?.scheduleDefinition}
          selectedKeys={selectedKeys}
          onToggle={handleToggle}
          searchTerm={searchTerm}
          onError={handleGridError}
          className="period-selector-organism__grid"
        />
      </div>

      {/* Bottom: selection bar */}
      <SelectionBarAtom
        selections={selectionBarItems}
        onRemove={handleRemove}
        onCancel={onCancel}
        onApply={handleApply}
        applying={applying}
      />
    </div>
  );
};

export default PeriodSelectorOrganism;
